import { useEffect, useState } from 'react'
import {
  Building2, Check, Loader2, PartyPopper, Radar, Radio, Save, Search, Settings2, Sparkles, X,
} from 'lucide-react'
import { Drawer, Select, adminMutate, apiGet, useToast } from '../admin/primitives.js'
import { Field, TextInput, NumberInput } from '../admin/form.js'
import { ConfirmDialog } from '../auth/Dialog.js'

/**
 * WIZARD D'AJOUT ET D'ÉDITION D'UN ÉTABLISSEMENT SURVEILLÉ.
 *
 * Pas un formulaire dense : un parcours progressif, une décision par écran.
 * Rien n'est écrit en base avant la validation finale — un wizard abandonné
 * ne laisse aucune venue fantôme, une édition abandonnée ne laisse pas une
 * fiche à moitié modifiée.
 *
 * LE BOUTON DE VALIDATION VIT DANS LE FOOTER DU TIROIR (v4).
 * Il était auparavant posé en fin de contenu, donc DANS la zone qui défile :
 * sur une étape longue — quatorze recherches, six sources — il passait sous
 * le pli et l'utilisateur ne trouvait pas comment terminer. Le footer du
 * Drawer est fixe : le CTA est visible à tout moment, quelle que soit la
 * hauteur de l'écran (§4).
 */

type Step = 0 | 1 | 2 | 3 | 4 | 5
/**
 * Un libellé PAR PANNEAU, dans l'ordre des panneaux. La liste précédente en
 * comptait six pour six écrans, mais décalés d'un cran à partir du
 * troisième : le panneau « Confirmer l'établissement » s'annonçait
 * « Sources », et celui des sources s'annonçait « Recherches ».
 */
const STEPS = ['Recherche', 'Résultats', 'Établissement', 'Sources', 'Recherches', 'Confirmation']
/** En édition, la recherche et le choix du candidat n'ont plus lieu d'être. */
const EDIT_FIRST_STEP: Step = 2

interface Candidate {
  name: string; url: string; hostname: string; title: string | null
  evidence: string[]; confidence: 'HIGH' | 'MEDIUM' | 'LOW'
}
interface Probe {
  kind: string; label: string; url: string | null; found: boolean; detail: string
}
interface QueryGroups {
  [intent: string]: { label: string; queries: { template: string; priority: string }[] }
}
/** Sources et requêtes déjà en base — en édition on les active/désactive. */
interface ExistingSource { id: string; type: string; label: string | null; url: string | null; enabled: boolean; state: string }
interface ExistingQuery { id: string; query_template: string; query_type: string; enabled: boolean }
interface VenueDetail {
  venue: {
    id: string; name: string; display_name: string | null; city: string | null
    category: string; competition_class: string; priority_level: string
    distance_km: number | null; custom_terms: string[] | null
  }
  sources: ExistingSource[]
  queries: ExistingQuery[]
}

const CATEGORY_OPTIONS = [
  { value: 'NIGHTCLUB', label: 'Boîte de nuit' }, { value: 'CLUB', label: 'Club' },
  { value: 'BAR_NIGHTLIFE', label: 'Bar nightlife' }, { value: 'BAR', label: 'Bar' },
  { value: 'CONCERT_VENUE', label: 'Salle de concert' }, { value: 'CULTURAL_VENUE', label: 'Lieu culturel' },
  { value: 'STUDENT_EVENT_ORGANIZER', label: 'Organisateur étudiant / BDE' },
  { value: 'PROMOTER', label: 'Organisateur' }, { value: 'FESTIVAL', label: 'Festival' },
  { value: 'OTHER', label: 'Autre' },
]
const CLASS_OPTIONS = [
  { value: 'DIRECT', label: 'Concurrence directe' },
  { value: 'ADJACENT', label: 'Nightlife adjacente' },
  { value: 'INDIRECT', label: 'Concurrence indirecte' },
  { value: 'CONTEXTUAL', label: 'Contextuel (salle, culture)' },
]
const PRIORITY_OPTIONS = [
  { value: 'HIGH', label: 'Haute' }, { value: 'NORMAL', label: 'Normale' }, { value: 'LOW', label: 'Basse' },
]
const SIGNAL_CHOICES = [
  { key: 'EVENT', label: 'Soirées' },
  { key: 'FREE_ENTRY', label: 'Entrée gratuite' },
  { key: 'FREE_ENTRY_GENDER', label: 'Gratuit filles' },
  { key: 'GUESTLIST', label: 'Guest list' },
  { key: 'OPEN_BAR', label: 'Open bar' },
  { key: 'DISCOUNT', label: 'Promotions' },
  { key: 'SPECIAL_GUEST', label: 'DJ invité' },
  { key: 'STUDENT_EVENT', label: 'BDE / étudiant' },
  { key: 'SOLD_OUT', label: 'Sold out' },
  { key: 'CANCELLATION', label: 'Annulation' },
  { key: 'SCHEDULE_CHANGE', label: 'Changement d’horaires' },
]
/** Ce que chaque source peut réellement apporter (§23) — jamais une promesse. */
const SOURCE_VALUE: Record<string, string> = {
  WEBSITE: 'Programmation officielle et informations pratiques.',
  EVENT_PAGE: 'Agenda détaillé : dates, line-up, tarifs annoncés.',
  RSS: 'Nouvelles publications dès leur parution.',
  ICS: 'Dates structurées, fiables et datées.',
  PUBLIC_TICKETING: 'Mises en vente, sold out, tarifs.',
  SOCIAL_REFERENCE: 'Promotions, guests, horaires annoncés publiquement.',
  SEARCH_ENGINE: 'Recherche large — nécessite un moteur configuré.',
}

/** Ce que le footer doit afficher pour l'étape courante. */
interface FooterSpec {
  back?: (() => void) | undefined
  backLabel?: string
  secondary?: { label: string; onClick: () => void } | undefined
  primary: {
    label: string
    busyLabel?: string
    icon?: 'search' | 'save' | 'next' | 'sparkles'
    onClick: () => void
    /** Motif de blocage — affiché, jamais silencieux (§10). */
    blocked?: string | null
  }
}

export function VenueWizard({ open, onClose, onCreated, venueId, onScanRequested }: {
  open: boolean
  onClose: () => void
  onCreated: () => void
  /** Présent = mode ÉDITION. */
  venueId?: string | null
  /** « Scanner maintenant » proposé après création (§6). */
  onScanRequested?: ((id: string) => void) | undefined
}) {
  const editing = Boolean(venueId)
  const toast = useToast()
  const [step, setStep] = useState<Step>(0)
  const [busy, setBusy] = useState(false)
  const [askQuit, setAskQuit] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [done, setDone] = useState<{ id: string; name: string } | null>(null)

  // étape 1
  const [query, setQuery] = useState('')
  const [city, setCity] = useState('Rennes')
  const [candidates, setCandidates] = useState<Candidate[] | null>(null)
  const [searchNote, setSearchNote] = useState<string | null>(null)
  // étape 2
  const [chosen, setChosen] = useState<Candidate | null>(null)
  const [canonical, setCanonical] = useState('')
  const [display, setDisplay] = useState('')
  const [distance, setDistance] = useState(3)
  const [category, setCategory] = useState('NIGHTCLUB')
  const [klass, setKlass] = useState('DIRECT')
  const [priority, setPriority] = useState('HIGH')
  const [duplicate, setDuplicate] = useState<{ id: string; name: string } | null>(null)
  // étape 3
  const [probes, setProbes] = useState<Probe[] | null>(null)
  const [activeSources, setActiveSources] = useState<Set<string>>(new Set())
  /** Capacités réelles du provider social, affichées sur la carte Instagram. */
  const [igCaps, setIgCaps] = useState<Record<string, string> | null>(null)
  const [igResolve, setIgResolve] = useState<{ ok: boolean; message: string; matches: boolean | null } | null>(null)
  const [igBusy, setIgBusy] = useState(false)
  const [existingSources, setExistingSources] = useState<ExistingSource[]>([])
  const [sourceToggles, setSourceToggles] = useState<Record<string, boolean>>({})
  // étape 4
  const [groups, setGroups] = useState<QueryGroups | null>(null)
  const [disabledQueries, setDisabledQueries] = useState<Set<string>>(new Set())
  const [customQuery, setCustomQuery] = useState('')
  const [customList, setCustomList] = useState<string[]>([])
  const [existingQueries, setExistingQueries] = useState<ExistingQuery[]>([])
  const [queryToggles, setQueryToggles] = useState<Record<string, boolean>>({})
  // étape 5
  const [signals, setSignals] = useState<Set<string>>(new Set(SIGNAL_CHOICES.map((s) => s.key)))
  /** Instantané de départ : sert à savoir si l'édition a VRAIMENT changé quelque chose. */
  const [baseline, setBaseline] = useState<string>('')

  const editSnapshot = () => JSON.stringify({
    display, category, klass, priority, distance,
    signals: [...signals].sort(), sourceToggles, queryToggles, customList,
  })
  const dirty = editing
    ? baseline !== '' && editSnapshot() !== baseline
    : query.trim() !== '' || chosen !== null

  useEffect(() => {
    if (!open) {
      setStep(0); setQuery(''); setCandidates(null); setChosen(null); setProbes(null)
      setGroups(null); setDuplicate(null); setCustomList([]); setSearchNote(null)
      setDisabledQueries(new Set()); setActiveSources(new Set())
      setDone(null); setSaveError(null); setBaseline('')
      setExistingSources([]); setExistingQueries([]); setSourceToggles({}); setQueryToggles({})
      return
    }
    if (!venueId) return
    // ÉDITION : on charge l'état réel, on saute la recherche.
    setBusy(true)
    void apiGet<VenueDetail>(`/api/v1/soirees/market/venues/${venueId}`)
      .then((d) => {
        setCanonical(d.venue.name)
        setDisplay(d.venue.display_name ?? d.venue.name)
        setCity(d.venue.city ?? 'Rennes')
        setCategory(d.venue.category)
        setKlass(d.venue.competition_class)
        setPriority(d.venue.priority_level)
        // `numeric` PostgreSQL arrive en CHAÎNE (« 3.0 ») : sans conversion,
        // l'instantané de référence contient "3.0" là où le formulaire, une
        // fois normalisé, contient 3 — et l'édition se croit modifiée
        // d'entrée de jeu.
        setDistance(Number(d.venue.distance_km ?? 3))
        setSignals(new Set(d.venue.custom_terms?.length ? d.venue.custom_terms : SIGNAL_CHOICES.map((s) => s.key)))
        setExistingSources(d.sources)
        setExistingQueries(d.queries)
        setSourceToggles(Object.fromEntries(d.sources.map((s) => [s.id, s.enabled])))
        setQueryToggles(Object.fromEntries(d.queries.map((q) => [q.id, q.enabled])))
        setStep(EDIT_FIRST_STEP)
      })
      .catch(() => toast('error', 'Établissement introuvable.'))
      .finally(() => setBusy(false))
  }, [open, venueId]) // eslint-disable-line react-hooks/exhaustive-deps

  // L'instantané de référence est pris APRÈS le chargement, sinon toute
  // édition serait « modifiée » dès l'ouverture.
  useEffect(() => {
    if (editing && open && baseline === '' && existingSources.length + existingQueries.length >= 0 && canonical) {
      setBaseline(editSnapshot())
    }
  }, [editing, open, canonical, existingSources, existingQueries]) // eslint-disable-line react-hooks/exhaustive-deps

  const rechercher = async () => {
    if (query.trim().length < 2) return
    setBusy(true); setCandidates(null); setSearchNote(null)
    const r = await adminMutate<{ candidates: Candidate[]; note: string | null; error?: string }>(
      'POST', '/api/v1/soirees/market/venues/search', { query: query.trim(), city })
    setBusy(false)
    if (r.status === 200) {
      setCandidates(r.data?.candidates ?? [])
      setSearchNote(r.data?.note ?? null)
      setStep(1)
    } else toast('error', 'Recherche impossible.')
  }

  const choisir = async (c: Candidate | null) => {
    setChosen(c)
    const nom = c?.title?.trim() || query.trim()
    setCanonical(nom.toUpperCase())
    setDisplay(nom)
    const dup = await adminMutate<{ duplicate: { id: string; name: string } | null; error?: string }>(
      'POST', '/api/v1/soirees/market/venues/check-duplicate', { name: nom })
    setDuplicate(dup.data?.duplicate ?? null)
    setStep(2)
    if (c) void decouvrir(c.url)
    else setProbes([])
  }

  // Les capacités du provider sont chargées une fois : la carte Instagram
  // doit annoncer ce qu'on saura réellement en lire, pas une promesse.
  useEffect(() => {
    if (!open) return
    void apiGet<{ capabilities: Record<string, string> }>('/api/v1/market/social/instagram')
      .then((d) => setIgCaps(d.capabilities))
      .catch(() => setIgCaps(null))
  }, [open])

  /** §12 — vérifier que le compte existe et ressemble à l'établissement. */
  const verifierInstagram = async (handle: string) => {
    setIgBusy(true); setIgResolve(null)
    const r = await adminMutate<{ ok: boolean; message: string; matches_venue: boolean | null; error?: string }>(
      'POST', '/api/v1/market/social/instagram/resolve',
      { handle, venue_name: display || canonical })
    setIgBusy(false)
    if (r.status === 200 && r.data) {
      setIgResolve({ ok: r.data.ok, message: r.data.message, matches: r.data.matches_venue })
    } else setIgResolve({ ok: false, message: 'Vérification impossible.', matches: null })
  }

  const decouvrir = async (url: string) => {
    setBusy(true); setProbes(null)
    const r = await adminMutate<{ probes: Probe[]; error?: string }>(
      'POST', '/api/v1/soirees/market/venues/discover-sources', { url })
    setBusy(false)
    const found = r.data?.probes ?? []
    setProbes(found)
    setActiveSources(new Set(found.filter((p) => p.found).map((p) => p.kind)))
  }

  const chargerRequetes = async () => {
    // En édition les requêtes existent déjà : inutile d'en reproposer.
    if (editing) { setStep(4); return }
    setBusy(true)
    const r = await adminMutate<{ groups: QueryGroups; error?: string }>(
      'POST', '/api/v1/soirees/market/venues/propose-queries',
      { name: display || canonical, city, competition_class: klass })
    setBusy(false)
    setGroups(r.data?.groups ?? {})
    setStep(4)
  }

  const allQueries = (): { template: string; type: 'AUTO' | 'CUSTOM'; priority: 'HIGH' | 'NORMAL' | 'LOW' }[] => {
    const out: { template: string; type: 'AUTO' | 'CUSTOM'; priority: 'HIGH' | 'NORMAL' | 'LOW' }[] = []
    for (const g of Object.values(groups ?? {})) {
      for (const q of g.queries) {
        if (disabledQueries.has(q.template)) continue
        out.push({ template: q.template, type: 'AUTO', priority: q.priority as 'HIGH' | 'NORMAL' | 'LOW' })
      }
    }
    for (const c of customList) out.push({ template: c, type: 'CUSTOM', priority: 'NORMAL' })
    return out
  }

  const sourcesPayload = () => (probes ?? [])
    .filter((p) => p.found && activeSources.has(p.kind))
    .map((p) => ({
      type: p.kind === 'SOCIAL_REFERENCE' ? 'SOCIAL_REFERENCE' : p.kind,
      label: p.label, url: p.kind === 'SOCIAL_REFERENCE' ? null : p.url,
      platform: p.kind === 'SOCIAL_REFERENCE' ? 'instagram' : null,
      handle: p.kind === 'SOCIAL_REFERENCE' && p.url ? p.url.split('/').pop() ?? null : null,
    }))

  /** Nombre de sources / requêtes actives, quel que soit le mode. */
  const activeSourceCount = editing
    ? Object.values(sourceToggles).filter(Boolean).length
    : sourcesPayload().length
  const activeQueryCount = editing
    ? Object.values(queryToggles).filter(Boolean).length + customList.length
    : allQueries().length

  const creer = async () => {
    setBusy(true); setSaveError(null)
    const r = await adminMutate<{ ok?: boolean; id?: string; error?: string; message?: string }>(
      'POST', '/api/v1/soirees/market/venues/create', {
        name: canonical.trim(),
        display_name: display.trim() && display.trim() !== canonical.trim() ? display.trim() : null,
        city, website: chosen?.url ?? null, distance_km: distance,
        category, competition_class: klass, priority_level: priority,
        sources: sourcesPayload(),
        queries: allQueries(),
        watched_signal_types: [...signals],
      })
    setBusy(false)
    if (r.status === 200 && r.data?.id) {
      onCreated()
      setDone({ id: r.data.id, name: display || canonical })
    } else if (r.status === 409) {
      // §7 — le brouillon est conservé : on reste sur la confirmation.
      setSaveError(r.data?.message ?? 'Cet établissement est déjà surveillé.')
    } else {
      setSaveError(r.data?.message ?? "Enregistrement impossible. Vos choix sont conservés — réessayez.")
    }
  }

  const enregistrer = async () => {
    if (!venueId) return
    setBusy(true); setSaveError(null)
    const r = await adminMutate<{ ok?: boolean; changed?: number; error?: string; message?: string }>(
      'PATCH', `/api/v1/soirees/market/venues/${venueId}/edit`, {
        display_name: display.trim() && display.trim() !== canonical.trim() ? display.trim() : null,
        category, competition_class: klass, priority_level: priority, distance_km: distance,
        watched_signal_types: [...signals],
        sources_enabled: sourceToggles,
        queries_enabled: queryToggles,
        new_queries: customList,
      })
    setBusy(false)
    if (r.status === 200) {
      onCreated()
      toast('ok', `« ${display || canonical} » a été mis à jour.`)
      onClose()
    } else {
      setSaveError(r.data?.message ?? 'Enregistrement impossible. Vos modifications sont conservées.')
    }
  }

  const fermer = () => { if (dirty && !done) setAskQuit(true); else onClose() }

  /* ─────────────────────── FOOTER : le CTA, toujours visible ───────────── */
  const footerSpec = (): FooterSpec | null => {
    if (done) return null
    switch (step) {
      case 0:
        return {
          primary: {
            label: 'Rechercher', busyLabel: 'Recherche…', icon: 'search',
            onClick: () => void rechercher(),
            blocked: query.trim().length < 2 ? 'Saisissez au moins deux caractères.' : null,
          },
        }
      case 1:
        return {
          back: () => setStep(0),
          secondary: { label: 'Ajouter sans site', onClick: () => void choisir(null) },
          primary: {
            label: 'Continuer', icon: 'next', onClick: () => setStep(2),
            blocked: chosen === null ? 'Choisissez un résultat, ou continuez sans site.' : null,
          },
        }
      case 2:
        return {
          ...(editing ? {} : { back: () => setStep(1) }),
          primary: {
            label: 'Continuer', icon: 'next', onClick: () => setStep(3),
            blocked: duplicate !== null
              ? `« ${duplicate.name} » est déjà surveillé.`
              : canonical.trim().length < 2 ? 'Le nom canonique est obligatoire.' : null,
          },
        }
      case 3:
        return {
          back: () => setStep(2),
          primary: {
            label: 'Continuer', icon: 'next', busyLabel: 'Analyse…',
            onClick: () => void chargerRequetes(), blocked: null,
          },
        }
      case 4:
        return {
          back: () => setStep(3),
          primary: { label: 'Continuer', icon: 'next', onClick: () => setStep(5), blocked: null },
        }
      case 5:
        return {
          back: () => setStep(4),
          primary: editing
            ? {
                label: 'Enregistrer les modifications', busyLabel: 'Enregistrement…', icon: 'save',
                onClick: () => void enregistrer(),
                blocked: dirty ? null : 'Aucune modification à enregistrer.',
              }
            : {
                label: 'Ajouter à la veille', busyLabel: 'Ajout en cours…', icon: 'sparkles',
                onClick: () => void creer(),
                blocked: canonical.trim().length < 2 ? 'Le nom canonique est obligatoire.' : null,
              },
        }
    }
  }

  const spec = footerSpec()
  const icon = (k: FooterSpec['primary']['icon']) =>
    k === 'search' ? <Search size={14} /> : k === 'save' ? <Save size={14} />
      : k === 'sparkles' ? <Sparkles size={14} /> : null

  const footer = spec ? (
    <div className="vw-foot">
      {spec.primary.blocked ? <p className="vw-foot__why">{spec.primary.blocked}</p> : null}
      <div className="vw-foot__btns">
        {spec.back ? (
          <button type="button" className="lc-btn lc-btn--ghost" onClick={spec.back} disabled={busy}>
            {spec.backLabel ?? 'Retour'}
          </button>
        ) : null}
        {spec.secondary ? (
          <button type="button" className="lc-btn" onClick={spec.secondary.onClick} disabled={busy}>
            {spec.secondary.label}
          </button>
        ) : null}
        <button
          type="button"
          className="lc-btn lc-btn--primary vw-foot__cta"
          /* Anti double-clic (§5) : `busy` suffit — le bouton reste monté,
             donc jamais de disparition qui ferait douter d'un clic pris. */
          disabled={busy || spec.primary.blocked !== null}
          onClick={spec.primary.onClick}
        >
          {busy
            ? <><Loader2 size={14} className="vw-spin" /> {spec.primary.busyLabel ?? 'Un instant…'}</>
            : <>{icon(spec.primary.icon)} {spec.primary.label}</>}
        </button>
      </div>
    </div>
  ) : null

  return (
    <>
      <Drawer
        open={open}
        title={editing ? 'Modifier l’établissement' : 'Ajouter un établissement'}
        onClose={fermer}
        footer={footer}
      >
        {done ? (
          /* ─── SUCCÈS (§6) : jamais un wizard terminé qui reste muet ─── */
          <div className="vw-done">
            <span className="vw-done__icon" aria-hidden="true"><PartyPopper size={30} /></span>
            <h3 className="vw-title">{done.name} a été ajouté à la veille.</h3>
            <p className="lc-hint">
              La première collecte partira au prochain créneau. Vous pouvez aussi la déclencher tout de suite.
            </p>
            <div className="vw-done__actions">
              <button type="button" className="lc-btn lc-btn--primary"
                onClick={() => { onScanRequested?.(done.id); onClose() }}>
                <Radio size={14} /> Scanner maintenant
              </button>
              <button type="button" className="lc-btn" onClick={onClose}>Ouvrir l’établissement</button>
              <button type="button" className="lc-btn lc-btn--ghost" onClick={onClose}>Fermer</button>
            </div>
          </div>
        ) : (
          <>
            <ol className="vw-steps" aria-label="Étapes">
              {STEPS.map((s, i) => (
                editing && i < EDIT_FIRST_STEP ? null : (
                  <li key={s} className={`vw-step${i === step ? ' vw-step--current' : ''}${i < step ? ' vw-step--done' : ''}`}>
                    <span className="vw-step__num">{i < step ? <Check size={12} /> : i + 1 - (editing ? EDIT_FIRST_STEP : 0)}</span>
                    <span className="vw-step__label">{s}</span>
                  </li>
                )
              ))}
            </ol>

            <div className="vw-panel" key={step}>
              {/* ─── 1. RECHERCHE ─── */}
              {step === 0 ? (
                <>
                  <h3 className="vw-title"><Search size={16} aria-hidden="true" /> Rechercher un établissement</h3>
                  <p className="lc-hint">Nom, lieu ou mot-clé. Gestion interroge les sources publiques — rien n'est enregistré à ce stade.</p>
                  <Field label="Nom de l'établissement">
                    <TextInput value={query} onChange={setQuery} placeholder="Satori" maxLength={80} ariaLabel="Nom de l'établissement" />
                  </Field>
                  <Field label="Ville">
                    <TextInput value={city} onChange={setCity} maxLength={60} ariaLabel="Ville" />
                  </Field>
                </>
              ) : null}

              {/* ─── 2. CANDIDATS ─── */}
              {step === 1 ? (
                <>
                  <h3 className="vw-title"><Building2 size={16} aria-hidden="true" /> Résultats</h3>
                  {searchNote ? <p className="lc-hint">{searchNote}</p> : null}
                  {(candidates ?? []).length === 0 ? (
                    <p className="gd-calm">Aucun site public trouvé. Vous pouvez l'ajouter manuellement — la couverture sera partielle et dite comme telle.</p>
                  ) : (
                    <div className="vw-cards">
                      {(candidates ?? []).map((c) => (
                        <div key={c.url} className={`vw-card vw-card--${c.confidence.toLowerCase()}${chosen?.url === c.url ? ' vw-card--chosen' : ''}`}>
                          <span className="vw-card__head">
                            <strong>{c.title ?? c.hostname}</strong>
                            <span className="vw-badge">{c.confidence === 'HIGH' ? 'Confiance élevée' : c.confidence === 'MEDIUM' ? 'À vérifier' : 'Faible'}</span>
                          </span>
                          <span className="lc-hint">{c.hostname}</span>
                          <ul className="vw-evidence">{c.evidence.map((e, i) => <li key={i}>{e}</li>)}</ul>
                          <button type="button" className="lc-btn" onClick={() => void choisir(c)}>
                            {chosen?.url === c.url ? 'Choisi' : 'Choisir'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : null}

              {/* ─── 3. IDENTITÉ ─── */}
              {step === 2 ? (
                <>
                  <h3 className="vw-title">
                    <Building2 size={16} aria-hidden="true" /> {editing ? 'Identité et positionnement' : "Confirmer l'établissement"}
                  </h3>
                  {duplicate ? (
                    <p className="vw-dup">
                      ⚠ « {duplicate.name} » est déjà surveillé — inutile de le créer deux fois.
                    </p>
                  ) : null}
                  <Field label="Nom canonique" helper="Identité interne : sert au rapprochement et à la déduplication. Ne change pas en édition.">
                    <TextInput value={canonical} onChange={setCanonical} maxLength={120}
                      ariaLabel="Nom canonique" disabled={editing} />
                  </Field>
                  <Field label="Nom d'affichage" helper="Ce que vous verrez à l'écran. Modifiable sans rien casser.">
                    <TextInput value={display} onChange={setDisplay} maxLength={120} ariaLabel="Nom d'affichage" />
                  </Field>
                  <div className="vw-grid">
                    <Field label="Type"><Select value={category} options={CATEGORY_OPTIONS} ariaLabel="Type" onChange={setCategory} /></Field>
                    <Field label="Concurrence"><Select value={klass} options={CLASS_OPTIONS} ariaLabel="Classe de concurrence" onChange={setKlass} /></Field>
                    <Field label="Priorité"><Select value={priority} options={PRIORITY_OPTIONS} ariaLabel="Priorité" onChange={setPriority} /></Field>
                    <Field label="Distance (km)">
                      <NumberInput value={distance} onChange={(v) => setDistance(Number(v) || 0)} min={0} max={100} unit="km" ariaLabel="Distance" />
                    </Field>
                  </div>
                </>
              ) : null}

              {/* ─── 4. SOURCES ─── */}
              {step === 3 ? (
                <>
                  <h3 className="vw-title"><Radar size={16} aria-hidden="true" /> {editing ? 'Sources surveillées' : 'Sources détectées'}</h3>
                  {editing ? (
                    existingSources.length === 0 ? (
                      <p className="gd-calm">Aucune source configurée — la veille reste partielle, et Gestion le dit.</p>
                    ) : (
                      <div className="vw-sources">
                        {existingSources.map((s) => (
                          <label key={s.id} className="vw-source">
                            <input type="checkbox" checked={sourceToggles[s.id] ?? false}
                              onChange={() => setSourceToggles((p) => ({ ...p, [s.id]: !p[s.id] }))}
                              aria-label={`Activer ${s.label ?? s.type}`} />
                            <span className="vw-source__body">
                              <span className="vw-source__label">{s.label ?? s.type} <em className="lc-hint">{s.state}</em></span>
                              <span className="lc-hint">{s.url ?? '—'}</span>
                              <span className="lc-hint vw-source__value">{SOURCE_VALUE[s.type] ?? ''}</span>
                            </span>
                          </label>
                        ))}
                        <p className="lc-hint">Désactiver conserve l'historique de collecte ; seule la suppression le perd.</p>
                      </div>
                    )
                  ) : probes === null ? (
                    <div className="so-skel" aria-busy="true"><span /><span /><span /></div>
                  ) : probes.length === 0 ? (
                    <p className="gd-calm">Aucune source publique détectée — la veille restera partielle, et Gestion le dira.</p>
                  ) : (
                    <div className="vw-sources">
                      {probes.map((p) => (
                        <label key={p.kind} className={`vw-source${p.found ? '' : ' vw-source--none'}`}>
                          <input type="checkbox" disabled={!p.found}
                            checked={activeSources.has(p.kind)}
                            onChange={() => setActiveSources((prev) => {
                              const n = new Set(prev)
                              if (n.has(p.kind)) n.delete(p.kind); else n.add(p.kind)
                              return n
                            })}
                            aria-label={`Activer ${p.label}`} />
                          <span className="vw-source__body">
                            <span className="vw-source__label">{p.label} {p.found ? <em className="vw-ok">détectée</em> : <em className="lc-hint">—</em>}</span>
                            <span className="lc-hint">{p.detail}</span>
                            {p.found ? <span className="lc-hint vw-source__value">{SOURCE_VALUE[p.kind] ?? ''}</span> : null}
                            {/* §38 — la carte Instagram annonce le handle ET
                                ce que le fournisseur permet réellement d'en
                                lire. Une capacité absente est barrée, pas tue. */}
                            {p.kind === 'SOCIAL_REFERENCE' && p.found ? (
                              <span className="vw-ig">
                                <span className="vw-ig__caps">
                                  {(['POSTS', 'REELS', 'STORIES'] as const).map((k) => {
                                    const state = igCaps?.[k] ?? 'UNKNOWN'
                                    const yes = state === 'SUPPORTED' || state === 'PARTIAL'
                                    return (
                                      <em key={k} className={`vw-ig__cap vw-ig__cap--${yes ? 'yes' : 'no'}`}>
                                        {k === 'POSTS' ? 'Publications' : k === 'REELS' ? 'Reels' : 'Stories'}
                                      </em>
                                    )
                                  })}
                                </span>
                                {igCaps?.['STORIES'] === 'NOT_SUPPORTED' ? (
                                  <span className="lc-hint">
                                    Les Stories d’un compte tiers ne sont accessibles par aucune voie officielle.
                                  </span>
                                ) : null}
                                <button type="button" className="lc-btn" disabled={igBusy}
                                  onClick={(e) => { e.preventDefault(); void verifierInstagram(p.url ?? '') }}>
                                  {igBusy
                                    ? <><Loader2 size={13} className="vw-spin" /> Vérification…</>
                                    : <>Vérifier ce compte</>}
                                </button>
                                {igResolve ? (
                                  <span className={igResolve.ok ? 'vw-ig__resolved' : 'vw-ig__warn'}>
                                    {igResolve.message}
                                    {igResolve.ok && igResolve.matches === false
                                      ? ' ⚠ Le nom ne correspond pas à l’établissement — à confirmer.'
                                      : ''}
                                  </span>
                                ) : null}
                              </span>
                            ) : null}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </>
              ) : null}

              {/* ─── 5. REQUÊTES ─── */}
              {step === 4 ? (
                <>
                  <h3 className="vw-title"><Search size={16} aria-hidden="true" /> Recherches de veille</h3>
                  {editing ? (
                    <>
                      <p className="lc-hint">Décochez ce qui n'a plus de sens ; ajoutez ce qui manque.</p>
                      <div className="vw-group">
                        {existingQueries.map((q) => (
                          <label key={q.id} className="vw-query">
                            <input type="checkbox" checked={queryToggles[q.id] ?? false}
                              onChange={() => setQueryToggles((p) => ({ ...p, [q.id]: !p[q.id] }))}
                              aria-label={`Activer « ${q.query_template} »`} />
                            <span>{q.query_template}</span>
                            <em className="lc-hint">{q.query_type === 'AUTO' ? 'auto' : 'perso'}</em>
                          </label>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="lc-hint">Générées automatiquement selon le type d'établissement. Décochez ce qui n'a pas de sens.</p>
                      {Object.entries(groups ?? {}).map(([intent, g]) => (
                        <div key={intent} className="vw-group">
                          <h4 className="so-class-title">{g.label}</h4>
                          {g.queries.map((q) => (
                            <label key={q.template} className="vw-query">
                              <input type="checkbox" checked={!disabledQueries.has(q.template)}
                                onChange={() => setDisabledQueries((prev) => {
                                  const n = new Set(prev)
                                  if (n.has(q.template)) n.delete(q.template); else n.add(q.template)
                                  return n
                                })}
                                aria-label={`Activer « ${q.template} »`} />
                              <span>{q.template}</span>
                              <em className="lc-hint">auto{q.priority === 'HIGH' ? ' · prioritaire' : ''}</em>
                            </label>
                          ))}
                        </div>
                      ))}
                    </>
                  )}
                  {customList.length > 0 ? (
                    <div className="vw-group">
                      <h4 className="so-class-title">{editing ? 'À ajouter' : 'Personnalisées'}</h4>
                      {customList.map((c) => (
                        <label key={c} className="vw-query">
                          <input type="checkbox" checked readOnly aria-label={c} />
                          <span>{c}</span>
                          <button type="button" className="lc-btn lc-btn--icon" aria-label={`Retirer ${c}`}
                            onClick={() => setCustomList((l) => l.filter((x) => x !== c))}><X size={12} /></button>
                        </label>
                      ))}
                    </div>
                  ) : null}
                  <span className="vw-add">
                    <TextInput value={customQuery} onChange={setCustomQuery} placeholder="Ajouter une recherche"
                      maxLength={200} ariaLabel="Nouvelle recherche" />
                    <button type="button" className="lc-btn" disabled={customQuery.trim().length < 3}
                      onClick={() => { setCustomList((l) => [...l, customQuery.trim()]); setCustomQuery('') }}>Ajouter</button>
                  </span>
                </>
              ) : null}

              {/* ─── 6. SIGNAUX + RÉCAP ─── */}
              {step === 5 ? (
                <>
                  <h3 className="vw-title"><Settings2 size={16} aria-hidden="true" /> Ce que Gestion surveille</h3>
                  <div className="vw-signals">
                    {SIGNAL_CHOICES.map((s) => (
                      <label key={s.key} className="vw-signal">
                        <input type="checkbox" checked={signals.has(s.key)}
                          onChange={() => setSignals((prev) => {
                            const n = new Set(prev)
                            if (n.has(s.key)) n.delete(s.key); else n.add(s.key)
                            return n
                          })}
                          aria-label={s.label} />
                        <span>{s.label}</span>
                      </label>
                    ))}
                  </div>

                  <div className="vw-recap">
                    <h4 className="so-class-title">Récapitulatif</h4>
                    <dl className="lc-kv">
                      <div><dt>Établissement</dt><dd>{display || canonical}</dd></div>
                      <div><dt>Type</dt><dd>{CATEGORY_OPTIONS.find((c) => c.value === category)?.label}</dd></div>
                      <div><dt>Concurrence</dt><dd>{CLASS_OPTIONS.find((c) => c.value === klass)?.label}</dd></div>
                      <div><dt>Sources actives</dt><dd>{activeSourceCount}</dd></div>
                      <div><dt>Recherches</dt><dd>{activeQueryCount}</dd></div>
                      <div><dt>Signaux surveillés</dt><dd>{signals.size}</dd></div>
                    </dl>
                  </div>
                  {saveError ? (
                    /* §7 — l'erreur s'affiche ICI, le brouillon reste intact. */
                    <p className="vw-dup" role="alert">{saveError}</p>
                  ) : null}
                </>
              ) : null}
            </div>
          </>
        )}
      </Drawer>

      <ConfirmDialog
        open={askQuit}
        title={editing ? 'Abandonner les modifications ?' : "Abandonner l'ajout ?"}
        confirmLabel="Abandonner"
        cancelLabel="Continuer l’édition"
        onConfirm={() => { setAskQuit(false); onClose() }}
        onClose={() => setAskQuit(false)}
      >
        <p>
          {editing
            ? "Rien n'a été enregistré : l'établissement restera tel qu'il est aujourd'hui."
            : "Rien n'a été enregistré : l'établissement ne sera pas créé."}
        </p>
      </ConfirmDialog>
    </>
  )
}
